// Pushing an arrangement into an Audiotool document — the host-agnostic core.
//
// No Node and no Live imports: the extension (push.ts, for a Live Set and for
// seeds) and the viz app (in the browser, for seeds) both run exactly this.
// What differs by host is injected: how bytes are fetched, and how audio is
// hashed into a content tag (node:crypto in the extension, Web Crypto in the
// browser — same algorithm, so the same bytes get the same tag everywhere).
//
// Two ways a seed carries audio:
//   • per-clip (current): each clip's audioKey is a clip-render of exactly
//     what it plays — one region per clip, at its beat (seedInputs);
//   • stems (older seeds): one full-track render per lane from beat 0 — one
//     region per lane spanning the stem's length (stemSeedInputs). The
//     renders already hold the silence between clips.

import { withRenders } from "../../../shared/sync/clip-render.js";
import { withSoundLanes } from "../../../shared/audiotool/sound-lanes.js";
import { planPush, type LaneMix, type PushAudio, type PushPlacement, type PushWarning } from "../../../shared/audiotool/push-plan.js";
import {
  reconcilePush,
  type PushLedger,
  type PushNotice,
  type SampleLibrary,
} from "../../../shared/audiotool/push-reconcile.js";
import { audioDurationSeconds } from "../../../shared/audiotool/audio-duration.js";
import { lanesOf, type CanonicalClip, type CanonicalSet } from "../../../shared/sync/canonical.js";
import { snapshotAsCanonicalSet } from "../../../shared/sync/snapshot-set.js";
import type { Snapshot } from "../../../shared/seed-history.js";
import { readDocState } from "./read.js";
import type { NexusDoc } from "./transact.js";
import { uploadMissing, type SampleUploader, type UploadReport } from "./uploads.js";
import { reportToAll, reportToUpdate, type PushReporter } from "./progress.js";
import { resolveInstruments, type GakkiPreset, type PresetClient } from "./instruments.js";
import { applyDelta, type WriteResult } from "./write.js";

export type ProgressUpdate = (text: string, percent?: number) => Promise<void>;
/** Content tag of some audio: 20 hex chars (80 bits) of its sha-256. */
export type AudioTagger = (bytes: Uint8Array) => string | Promise<string>;

export interface PushInputs {
  set: CanonicalSet;
  /** Per lane (lanesOf order). */
  mix: LaneMix[];
  /**
   * tag → the bytes and a human name for the upload. `source` marks bytes
   * that are the ORIGINAL a clip windows into (a browser-saved warped clip),
   * with their musical length; absent ⇒ a clip render (push-plan.ts PushAudio).
   */
  audio: Map<string, { bytes: Uint8Array; displayName: string; source?: { sourceBeats: number } }>;
  placements: PushPlacement[];
  /** Things the source itself couldn't provide (failed renders, missing R2 objects). */
  warnings: string[];
}

export interface PushTarget {
  doc: NexusDoc;
  ledger: PushLedger;
  library: SampleLibrary;
  upload: SampleUploader;
  /**
   * Where the GM instrument presets for new MIDI lanes come from. Optional:
   * without it every note lane gets the stand-in synth, which is what happens
   * anyway when the catalogue can't be reached.
   */
  presets?: PresetClient;
}

export interface PushReport {
  uploads: UploadReport;
  write: WriteResult;
  planWarnings: PushWarning[];
  notices: PushNotice[];
  sourceWarnings: string[];
  /** Ops sent — 0 means the document already matched. */
  ops: number;
}

/**
 * One reporter from whichever the caller gave. `update` is the prose-and-percent
 * listener the Live panel has always used; `report` is the event stream the web
 * screen renders. Both may be present — the events drive both.
 */
function reporterOf(opts: { update?: ProgressUpdate; report?: PushReporter }): PushReporter {
  return reportToAll(opts.report, opts.update ? reportToUpdate(opts.update) : undefined);
}

export function mixOf(s: Snapshot): LaneMix[] {
  return [...s.audioTracks, ...(s.midiTracks ?? [])].map((t) => ({ volume: t.volume, panning: t.panning }));
}

/** Plan → reconcile → upload → write. Updates `target.ledger` and `.library`. */
export async function pushArrangement(
  inputs: PushInputs,
  target: PushTarget,
  opts: { update?: ProgressUpdate; report?: PushReporter; signal?: AbortSignal } = {},
): Promise<PushReport> {
  const say = reporterOf(opts);
  const audio = new Map<string, PushAudio>();
  for (const [tag, a] of inputs.audio) {
    audio.set(
      tag,
      a.source
        ? { tag, displayName: a.displayName, kind: "source-file", sourceBeats: a.source.sourceBeats }
        : { tag, displayName: a.displayName, kind: "clip-render" },
    );
  }
  const plan = planPush(inputs.set, audio, inputs.placements, { signature: null, mix: inputs.mix });
  const delta = reconcilePush(plan, target.ledger, readDocState(target.doc), target.library);

  const uploads = await uploadMissing(delta.uploads, (t) => inputs.audio.get(t)?.bytes, target.library, target.upload, {
    bpm: inputs.set.song.tempo,
    ...(opts.signal ? { signal: opts.signal } : {}),
    report: say,
  });

  // Before the transaction, deliberately: a batch holds the document lock for
  // its whole duration, and these are network calls.
  const instruments: ReadonlyMap<string, GakkiPreset> = target.presets
    ? await resolveInstruments(target.presets, delta)
    : new Map();

  say({ kind: "stage", stage: "writing" });
  const write = await applyDelta(target.doc, delta, target.ledger, target.library, { instruments });
  return {
    uploads,
    write,
    planWarnings: plan.warnings,
    notices: delta.notices,
    sourceWarnings: inputs.warnings,
    ops: delta.batches.reduce((n, b) => n + b.length, 0),
  };
}

/**
 * A seed's arrangement and its already-rendered clip audio as PushInputs.
 * `fetchBytes` downloads a signed URL; a clip whose object is missing is
 * reported, not fatal.
 */
export async function seedInputs(
  arrangement: Snapshot,
  clipAudioUrls: Record<string, string>,
  fetchBytes: (url: string) => Promise<Uint8Array>,
  tagOf: AudioTagger,
  opts: { update?: ProgressUpdate; report?: PushReporter } = {},
): Promise<PushInputs> {
  const say = reporterOf(opts);
  // MIDI tracks with bounces go out as their notes (muted) plus a lane
  // playing the bounces — the seed's sound, not a stand-in's (sound-lanes.ts).
  // A clip with a published render of its browser warp (markers, pitch, mode
  // knobs — Audiotool has no warp engine) is read through the render.
  arrangement = withSoundLanes(withRenders(arrangement));
  const set = snapshotAsCanonicalSet(arrangement);
  const audio = new Map<string, { bytes: Uint8Array; displayName: string; source?: { sourceBeats: number } }>();
  const placements: PushPlacement[] = [];
  const warnings: string[] = [];
  const byKey = new Map<string, string | null>();
  const keys = [...new Set(arrangement.audioTracks.flatMap((t) => t.arrangementClips.map((c) => c.audioKey).filter((k): k is string => !!k)))];
  let n = 0;
  for (const key of keys) {
    say({ kind: "stage", stage: "fetching-audio", done: ++n, total: keys.length });
    say({ kind: "sample", id: key, displayName: key, state: "downloading" });
    const url = clipAudioUrls[key];
    if (!url) {
      byKey.set(key, null);
      say({ kind: "sample", id: key, displayName: key, state: "missing" });
      continue;
    }
    try {
      const bytes = await fetchBytes(url);
      const tag = await tagOf(bytes);
      audio.set(tag, { bytes, displayName: tag });
      byKey.set(key, tag);
      say({ kind: "sample", id: key, displayName: key, state: "downloaded", bytes: bytes.byteLength, tag });
    } catch (e) {
      console.warn(`[Audiotool] couldn't fetch seed audio ${key}:`, e);
      byKey.set(key, null);
      say({ kind: "sample", id: key, displayName: key, state: "missing" });
    }
  }
  arrangement.audioTracks.forEach((t, laneIndex) =>
    t.arrangementClips.forEach((c, clipIndex) => {
      const tag = c.audioKey ? byKey.get(c.audioKey) : null;
      if (!tag) {
        if (c.audioKey) warnings.push(`${t.name}: "${c.name}" — its audio couldn't be fetched`);
        return;
      }
      audio.get(tag)!.displayName = c.name;
      say({ kind: "sample", id: c.audioKey!, displayName: `${t.name} — ${c.name}`, state: "downloaded", tag });
      placements.push({ laneIndex, clipIndex, tag });
    }),
  );
  markSourceAudio(arrangement, byKey, audio);
  if (lanesOf(set).length === 0) warnings.push("The seed has no tracks.");
  return { set, mix: mixOf(arrangement), audio, placements, warnings };
}

/**
 * A browser-saved WARPED clip's bytes are its original audio, not a render:
 * marked "source-file" with the audio's length in its OWN beats (its source
 * tempo), so the region windows and stretches it rather than playing it flat.
 * An unwarped browser clip plays from the top at its own speed — exactly a
 * render's semantics — so it stays a render. Bytes shared with a clip that IS
 * a render stay a render too (one kind per object).
 */
function markSourceAudio(
  arrangement: Snapshot,
  byKey: ReadonlyMap<string, string | null>,
  audio: Map<string, { bytes: Uint8Array; displayName: string; source?: { sourceBeats: number } }>,
): void {
  const verdict = new Map<string, number | null>(); // tag → source tempo, or null once any user is a render
  for (const t of arrangement.audioTracks) {
    for (const c of t.arrangementClips) {
      const tag = c.audioKey ? byKey.get(c.audioKey) : null;
      if (!tag) continue;
      const tempo = c.audioIs === "source" && c.warping ? (c.sourceTempo ?? arrangement.song.tempo) : null;
      if (!verdict.has(tag)) verdict.set(tag, tempo);
      else if (verdict.get(tag) !== tempo) verdict.set(tag, null);
    }
  }
  for (const [tag, tempo] of verdict) {
    const a = audio.get(tag);
    const seconds = a ? audioDurationSeconds(a.bytes) : null;
    if (a && tempo !== null && tempo > 0 && seconds) a.source = { sourceBeats: (seconds * tempo) / 60 };
  }
}

/**
 * An older, stem-shaped seed as PushInputs: each audio lane gets ONE region
 * from beat 0 spanning its stem (its length read from the file header).
 * Stems are matched to lanes by name first and track index second — the
 * index is the track's position in the producer's Set, which usually but not
 * always equals the snapshot's. MIDI lanes keep their notes.
 */
export async function stemSeedInputs(
  arrangement: Snapshot,
  stems: ReadonlyArray<{ name: string; trackIndex: number; url: string }>,
  fetchBytes: (url: string) => Promise<Uint8Array>,
  tagOf: AudioTagger,
  opts: { update?: ProgressUpdate; report?: PushReporter } = {},
): Promise<PushInputs> {
  const say = reporterOf(opts);
  const base = snapshotAsCanonicalSet(arrangement);
  const tempo = arrangement.song.tempo;
  const audio = new Map<string, { bytes: Uint8Array; displayName: string }>();
  const placements: PushPlacement[] = [];
  const warnings: string[] = [];
  const byName = new Map(arrangement.audioTracks.map((t, i) => [t.name, i]));
  const laneClips = new Map<number, CanonicalClip>();
  let n = 0;
  for (const stem of stems) {
    say({ kind: "stage", stage: "fetching-stems", done: ++n, total: stems.length });
    say({ kind: "sample", id: stem.name, displayName: stem.name, state: "downloading" });
    const lane = byName.get(stem.name) ?? (stem.trackIndex < arrangement.audioTracks.length ? stem.trackIndex : -1);
    if (lane < 0 || laneClips.has(lane)) {
      warnings.push(`Stem "${stem.name}" matches no track in the arrangement`);
      continue;
    }
    let bytes: Uint8Array;
    try {
      bytes = await fetchBytes(stem.url);
    } catch (e) {
      warnings.push(`Stem "${stem.name}" couldn't be fetched`);
      say({ kind: "sample", id: stem.name, displayName: stem.name, state: "missing" });
      console.warn(`[Audiotool] couldn't fetch stem ${stem.name}:`, e);
      continue;
    }
    const seconds = audioDurationSeconds(bytes);
    if (!seconds || !(tempo > 0)) {
      warnings.push(`Stem "${stem.name}" isn't a WAV or AIFF file`);
      say({ kind: "sample", id: stem.name, displayName: stem.name, state: "missing" });
      continue;
    }
    const beats = Math.round(((seconds * tempo) / 60) * 1e4) / 1e4;
    const tag = await tagOf(bytes);
    audio.set(tag, { bytes, displayName: stem.name });
    say({ kind: "sample", id: stem.name, displayName: stem.name, state: "downloaded", bytes: bytes.byteLength, tag });
    laneClips.set(lane, {
      name: stem.name, startTime: 0, duration: beats, startMarker: 0, endMarker: beats, looping: false,
      loopStart: 0, loopEnd: beats, color: 0, muted: false, filePath: "", warping: false, warpMode: { value: 0 },
    });
    placements.push({ laneIndex: lane, clipIndex: 0, tag });
  }
  const set: CanonicalSet = {
    ...base,
    audioTracks: base.audioTracks.map((t, i) => ({ ...t, arrangementClips: laneClips.has(i) ? [laneClips.get(i)!] : [] })),
  };
  return { set, mix: mixOf(arrangement), audio, placements, warnings };
}
